import {
  FC,
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import { useQueryClient } from 'react-query'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, UserModel } from './_models'
import * as authHelper from './AuthHelpers'

import { WithChildren } from '../../../../_metronic/helpers'
import { callLogout, applicantGetRecord } from './_requests'
import { array_fill_keys } from '../../../components/helpers'
import { pick } from 'lodash'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  lastIsSetsco: boolean | undefined
  hasRole: (findStr: any) => boolean
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  lastIsSetsco: false,
  setCurrentUser: () => {},
  hasRole: () => (false),
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const queryClient = useQueryClient();

  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [lastIsSetsco, setLastIsSetsco] = useState<boolean | undefined>(false);
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    // clear auth vars
    saveAuth(undefined)
    setCurrentUser(undefined)
    // clear JSESSIONID cookie
    document.cookie = 'JSESSIONID=; Max-Age=-99999999;';
    callLogout();
    // clear query cache
    queryClient.removeQueries();
    queryClient.clear();
  }

  const hasRole = (findStr: any) => {

    let targets: Array<string> = [];
    let got_match = false;

    if (Array.isArray(findStr)) {
      targets = findStr;
    } else if (typeof findStr === 'string') {
      targets = findStr.split(',');
    }

    if (targets.length > 0 && currentUser && currentUser.role) {
      try {
        currentUser.role.split(',').forEach( role => {
          targets.forEach( val => {
            if (typeof val === 'string' && role.trim() === val.trim()) {
              got_match = true;
            }
          })
        })
      } catch {
      // do nothing
      }
    }

    return got_match;
  }

  useEffect(() => {
    if (currentUser) {
      setLastIsSetsco(currentUser.isSetsco);
    }

  }, [currentUser]);

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, lastIsSetsco, hasRole, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser, currentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (isSetsco?: boolean) => {
      try {
        if (didRequest.current) return;
        let _isset = false;

        if (! isSetsco) {
          // get applicant record
          const{ data: record} = await applicantGetRecord();
          if (record) {
            Object.assign( record, { isSetsco: false});
            setCurrentUser(record)
            _isset = true;
          }
        }

        if ((! _isset) && auth) {
          const _user = {
            ...array_fill_keys(['id','email','role'], ''),
            requireReview: false,
            ...pick(auth, ['id','email','name','role','requireReview']),
            designation: '',
            isSetsco: (isSetsco === true)
          };
          setCurrentUser(_user);
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.loggedIn) {
      if (! currentUser) {
        requestUser(auth.isSetsco)
      } else {
        setShowSplashScreen(false)
      }
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}


export {AuthProvider, AuthInit, useAuth}
